import axios from "axios";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { ModuleFields } from "~/Utils/drilldownData";

// Define the option type for select fields
export interface OptionType {
  label: string;
  value: string;
}

// Create a type for fields that may have async options
export type AsyncSelectField = ModuleFields & {
  loadOptions?: () => Promise<OptionType[]>;
};

// Function to fetch data and transform to options
export const FetchSubModuleCentralized = async (value?: string, currentAirport?: string): Promise<OptionType[]> => {
  try {
    // --- CASE 1: Airport = ALL (merge both API responses) ---
    if (currentAirport?.toLowerCase() === "all" && !value) {
      const [parentRes, publicRes] = await Promise.all([
        axios.get(`${centralizedHost}/api/zone-parent`),
        axios.get(`${centralizedHost}/api/zone-child`),
      ]);

      const parentData = parentRes?.data || [];
      const publicData = publicRes?.data || [];

      // Apply your existing filtering rules
      const parentOptions = parentData
        .filter((item: any) => item.published === true && !["public", "passenger"].includes(item.name?.toLowerCase()))
        .map((item: any) => ({
          value: item.id,
          label: decodeHTML(item.name),
        }));

      const publicFiltered = publicData.filter((item: any) => {
        if (!item.published) return false;
        if (!value) return true;
        return item.parent_term_name?.toLowerCase() === value.toLowerCase();
      });

      const publicOptions = publicFiltered.map((item: any) => ({
        value: item.id,
        label: decodeHTML(item.name),
      }));

      return [...parentOptions, ...publicOptions];
    }

    // --- CASE 2:  value is public or passenger and currentairport is ''
    if (value) {
      const response = await axios.get(`${centralizedHost}/api/zone-child`);
      const data = response?.data || [];

      const filteredData = data.filter((item: any) => {
        if (!item.published) return false;
        return item.parent_term_name?.toLowerCase() === value.toLowerCase();
      });
      return filteredData.map((item: any) => ({
        value: item.id,
        label: decodeHTML(item.name),
      }));
    }
    // case 3 {value: undefined, currentAirport: ''}
    if (!value && !currentAirport) {
      const response = await axios.get(`${centralizedHost}/api/zone-parent`);
      const data = response?.data || [];
      const options = data
        .filter((item: any) => item.published === true && !["public", "passenger"].includes(item.name?.toLowerCase()))
        .map((item: any) => ({
          value: item.id,
          label: decodeHTML(item.name),
        }));
      return options;
    }
  } catch (error) {
    console.error("Failed to fetch zone options:", error);
    return [];
  }
};

const getAirportSlugById = async (airportId?: string): Promise<string> => {
  if (!airportId) return "all";

  const res = await axios.get(`${centralizedHost}/api/project-airport-drop-down`);
  const airports = res?.data || [];

  const matched = airports.find((item: any) => String(item.id) === String(airportId));

  return matched?.slug ?? "all";
};

export const FetchLocationAirport = async (currentAirport?: string): Promise<OptionType[]> => {
  try {
    let url = `${centralizedHost}/api/store-location-dropdown/all`;

    if (currentAirport) {
      const airportSlug = await getAirportSlugById(currentAirport);

      if (airportSlug && airportSlug !== "all") {
        url = `${centralizedHost}/api/store-location-dropdown/${airportSlug}`;
      }
    }

    const response = await axios.get(url);

    const data = response?.data?.data || [];

    const options = data.map((item: any) => ({
      value: item.id,
      label: decodeHTML(item.name),
    }));

    return options;
  } catch (error) {
    console.error("Failed to fetch location options:", error);
    return [];
  }
};
